// Call Stats Dashboard — domain types, filter params, and store shape.
// Mirrors docs/call-stats-dashboard.md (backend API contract).

export type TimeWindow = '1h' | '3h' | '6h' | '1d' | '7d' | 'custom';

export interface CallStatsFilters {
  window: TimeWindow;
  // ISO-8601 UTC; only meaningful when window === 'custom'.
  from?: string;
  to?: string;
  userId?: string;
  peerUserId?: string;
}

export type CallType = 'video' | 'sip';
export type CallStatus =
  | 'completed'
  | 'rejected'
  | 'no_answer'
  | 'disconnected'
  | 'failed'
  // in-flight calls (RING / IN_PROGRESS) the API surfaces in the log
  | 'ringing'
  | 'in_progress';
export type IceType = 'host' | 'srflx_stun' | 'relay_turn';
export type ScoreBand = 'excellent' | 'good' | 'fair' | 'poor';
export type QualityDimension = 'user' | 'duration' | 'participants';

export interface Participant {
  userId: string;
  displayName: string;
}

export interface TimeBucket {
  bucketStart: string; // ISO
  total: number;
  video: number;
  sip: number;
  failed: number;
}

export interface SummaryResponse {
  totalCalls: number;
  videoCalls: number;
  sipCalls: number;
  failedCalls: number;
  avgDurationSec: number;
  totalDurationSec: number;
  byType: { video: number; sip: number };
  // summary breakdown is terminal statuses only — in-flight calls aren't counted here
  byStatus: {
    completed: number;
    rejected: number;
    no_answer: number;
    disconnected: number;
    failed: number;
  };
  timeseries: TimeBucket[];
}

export interface UserSummary {
  userId: string;
  displayName: string;
  totalCalls: number;
  videoCalls: number;
  sipCalls: number;
  failedCalls: number;
  avgDurationSec: number;
}

export interface ByUserResponse {
  users: UserSummary[];
}

export interface CallLogRow {
  callId: string;
  startedAt: string; // ISO
  type: CallType;
  status: CallStatus;
  durationSec: number;
  participants: Participant[];
  // Quality fields — null from REST until the quality backend lands (mock fills them).
  experienceScore?: number | null;
  mos?: number | null;
  rttMs?: number | null;
  packetLossPct?: number | null;
  jitterMs?: number | null;
  bitrateKbps?: number | null;
  iceType?: IceType | null;
}

export interface PairSummary {
  userA: Participant;
  userB: Participant;
  totalCalls: number;
  video: number;
  sip: number;
  failed: number;
  avgDurationSec: number;
  calls: CallLogRow[];
}

export type FailureReason =
  'no_answer' | 'rejected' | 'disconnected' | 'setup_failed' | 'unknown';

export interface RankedFailureUser {
  userId: string;
  displayName: string;
  failedCalls: number;
  totalCalls: number;
  failureRatePct: number;
}

export interface FailuresResponse {
  totalFailed: number;
  byReason: Record<FailureReason, number>;
  rankedUsers: RankedFailureUser[];
}

export interface ConnectivityResponse {
  ice: { host: number; srflx_stun: number; relay_turn: number };
  hostPct: number;
  stunPct: number;
  turnPct: number;
}

export interface Scores {
  experience: number; // 0-100 (headline)
  video: number; // 0-100
  audio: number; // 0-100
  mos: number | null; // 1.0-5.0; null when no calls in the window are scored
  connectivity: number; // 0-100
  callsScored: number;
}

export interface DimensionBucket {
  key: string;
  label: string;
  calls: number;
  experience: number;
  video: number;
  audio: number;
  mos: number;
  connectivity: number;
}

export interface ByDimensionResponse {
  dimension: QualityDimension;
  buckets: DimensionBucket[];
}

export interface CallsPage {
  total: number;
  page: number;
  pageSize: number;
  rows: CallLogRow[];
}

export interface CallsQuery extends CallStatsFilters {
  type?: CallType;
  status?: CallStatus;
  page?: number;
  pageSize?: number;
}

export interface MetricSample {
  t: string; // ISO
  rttMs: number;
  lossPct: number;
  jitterMs: number;
  bitrateKbps: number;
  mos: number;
}

export interface CallDetail {
  callId: string;
  startedAt: string;
  durationSec: number;
  type: CallType;
  status: CallStatus;
  participants: Participant[];
  iceType: IceType | null;
  scores: Scores;
  metrics: {
    bitrateKbps: number;
    rttMs: number;
    packetLossPct: number;
    jitterMs: number;
    mos: number | null;
  };
  timeseries: MetricSample[];
}

// ---- Redux store shape ------------------------------------------------------

export interface Panel<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
  // true when the data is served from the mock adapter (no backend yet).
  mocked: boolean;
}

export interface CallStatsStore {
  filters: CallStatsFilters;
  summary: Panel<SummaryResponse>;
  byUser: Panel<ByUserResponse>;
  pair: Panel<PairSummary>;
  failures: Panel<FailuresResponse>;
  connectivity: Panel<ConnectivityResponse>;
  scores: Panel<Scores>;
  byDimension: Panel<ByDimensionResponse>;
  calls: Panel<CallsPage>;
  callDetail: Panel<CallDetail>;
}

export type PanelKey = Exclude<keyof CallStatsStore, 'filters'>;

export enum CallStatsActionTypes {
  SET_FILTERS = 'CALL_STATS_SET_FILTERS',
  PANEL_REQUEST = 'CALL_STATS_PANEL_REQUEST',
  PANEL_SUCCESS = 'CALL_STATS_PANEL_SUCCESS',
  PANEL_FAILURE = 'CALL_STATS_PANEL_FAILURE',
}

export type CallStatsAction =
  | {
      type: CallStatsActionTypes.SET_FILTERS;
      payload: Partial<CallStatsFilters>;
    }
  | { type: CallStatsActionTypes.PANEL_REQUEST; payload: { key: PanelKey } }
  | {
      type: CallStatsActionTypes.PANEL_SUCCESS;
      payload: { key: PanelKey; data: unknown; mocked: boolean };
    }
  | {
      type: CallStatsActionTypes.PANEL_FAILURE;
      payload: { key: PanelKey; error: string };
    };

// Helpers for score → band mapping (shared by score cards + tables).
export const scoreBand = (score: number): ScoreBand => {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
};

// Bands match the hub's JanusQualityMonitor::mosBand (Excellent ≥4.0,
// Good ≥3.5, Fair ≥3.0, else Poor) so the dashboard agrees with the device.
export const mosBand = (mos: number): ScoreBand => {
  if (mos >= 4.0) return 'excellent';
  if (mos >= 3.5) return 'good';
  if (mos >= 3.0) return 'fair';
  return 'poor';
};
